import api from '../../utils/api';

export const MEMBERSHIP_COVER_MEDIA_KEY = 'membership_home_card';
/** Wide strip for web desktop home only. */
export const MEMBERSHIP_COVER_DESKTOP_MEDIA_KEY = 'membership_home_card_desktop';

export interface MembershipPlanSpeciality {
  specialityId: string;
  name: string;
}

export interface MembershipPlanDoctor {
  vendorId: string;
  doctorName: string;
  clinicName?: string;
  specialityId: string;
  specialityName: string;
}

export interface MembershipPlan {
  membershipPlanId: string;
  name: string;
  title: string;
  type: 'Starter' | 'Silver' | 'Gold' | 'Platinum' | string;
  gpConsultFee?: number;
  vedikaCommissionPercent?: number;
  dedicatedAmbulanceNumber?: string;
  consultationCreditsTotal?: number;
  allowedSpecialities?: MembershipPlanSpeciality[];
  allowedDoctors?: MembershipPlanDoctor[];
  promoImageUrl?: string | null;
  promoImageMediaKey?: string | null;
  promoImageOrientation?: 'horizontal' | 'vertical';
  gstPercent?: number;
  gstMode?: 'inclusive' | 'exclusive';
  pricing?: {
    basePrice?: number;
    gstPercent?: number;
    gstMode?: 'inclusive' | 'exclusive';
    gstAmount?: number;
    subtotal?: number;
    payable?: number;
    showGst?: boolean;
  };
  price: number;
  duration: string;
  description?: string;
  isActive: boolean;
  isPopular?: boolean;
  sortOrder?: number;
  maxFamilyMembers?: number;
  consultationCreditsPerMonth?: number;
  consultationCreditCarryForward?: boolean;
  maxCarryForwardCredits?: number;
  storageQuotaBytes?: number;
  maxTransfersPerYear?: number;
  medicineDiscountPercent?: number;
  labTestDiscountPercent?: number;
  productDiscountPercent?: number;
  ambulancePriority?: boolean;
  emergencyPriority?: boolean;
  nutritionPlansAccess?: boolean;
  medicoLegalAssistance?: boolean;
  mediclaimAmount?: number;
  earlyAccess?: boolean;
  features?: Array<{ title?: string; value?: string; isIncluded?: boolean }>;
  highlights?: string[];
}

export interface MembershipDashboard {
  activeCount: number;
  expiredCount: number;
  cancelledCount: number;
  pendingTransfers: number;
  monthRevenue: number;
  planCounts: Array<{ planName?: string; count?: number }>;
}

export async function fetchMembershipDashboard() {
  const res = await api.get('/master-admin/membership/dashboard');
  return (res.data?.data || {}) as MembershipDashboard;
}

export async function fetchMembershipReports(params?: { from?: string; to?: string }) {
  const res = await api.get('/master-admin/membership/reports', { params });
  return res.data?.data;
}

export async function seedMembershipPlans() {
  const res = await api.post('/master-admin/membership/seed-plans');
  return res.data?.data;
}

export async function fetchMembershipGstSettings() {
  const res = await api.get('/master-admin/membership/gst-settings');
  return (res.data?.data || { gstPercent: 0, gstMode: 'exclusive' }) as {
    gstPercent: number;
    gstMode: 'inclusive' | 'exclusive';
  };
}

export async function updateMembershipGstSettings(payload: {
  gstPercent: number;
  gstMode: 'inclusive' | 'exclusive';
}) {
  const res = await api.put('/master-admin/membership/gst-settings', payload);
  return res.data;
}

export async function runMembershipExpiryJob() {
  const res = await api.post('/master-admin/membership/jobs/expire');
  return res.data?.data;
}

export async function fetchMembershipPlanDoctors(search?: string) {
  const res = await api.get('/master-admin/membership/doctors', {
    params: search ? { search, limit: 40 } : { limit: 40 },
  });
  return (res.data?.data || []) as Array<{
    vendorId: string;
    doctorName: string;
    clinicName?: string;
    specialities: Array<{ specialityId: string; name: string }>;
  }>;
}

export async function fetchMembershipPlans() {
  const res = await api.get('/master-admin/membership/plans');
  return (res.data?.data || []) as MembershipPlan[];
}

export async function createMembershipPlan(payload: Partial<MembershipPlan>) {
  const res = await api.post('/master-admin/membership/plans', payload);
  return res.data?.data as MembershipPlan;
}

export async function updateMembershipPlan(planId: string, payload: Partial<MembershipPlan>) {
  const res = await api.put(`/master-admin/membership/plans/${planId}`, {
    ...payload,
    rebuildFeatures: true,
  });
  return res.data?.data as MembershipPlan;
}

export function membershipPromoMediaKey(planId?: string | null) {
  return planId
    ? `membership_plan_promo_${planId}`
    : `membership_plan_promo_draft_${Date.now()}`;
}

export async function setMembershipPlanActive(planId: string, isActive: boolean) {
  const res = await api.patch(`/master-admin/membership/plans/${planId}/active`, { isActive });
  return res.data?.data as MembershipPlan;
}

export async function deleteMembershipPlan(planId: string, reason?: string) {
  const res = await api.delete(`/master-admin/membership/plans/${planId}`, { data: { reason } });
  return res.data;
}

export async function fetchUserMemberships(params?: {
  status?: string;
  planType?: string;
  userId?: string;
  q?: string;
  page?: number;
  pageSize?: number;
}) {
  const res = await api.get('/master-admin/membership/memberships', { params });
  return {
    rows: (res.data?.data || []) as any[],
    total: Number(res.data?.total || 0),
    page: Number(res.data?.page || 1),
    pageSize: Number(res.data?.pageSize || 20),
  };
}

export async function fetchUserMembershipDetail(membershipId: string) {
  const res = await api.get(`/master-admin/membership/memberships/${membershipId}`);
  return res.data?.data;
}

export async function manualActivateMembership(payload: {
  userId: string;
  membershipPlanId: string;
  reason?: string;
}) {
  const res = await api.post('/master-admin/membership/memberships/activate', payload);
  return res.data?.data;
}

export async function manualCancelMembership(membershipId: string, reason?: string) {
  const res = await api.post(`/master-admin/membership/memberships/${membershipId}/cancel`, {
    reason,
  });
  return res.data;
}

export async function manualExpireMembership(membershipId: string, reason?: string) {
  const res = await api.post(`/master-admin/membership/memberships/${membershipId}/expire`, {
    reason,
  });
  return res.data;
}

export async function adjustMembershipCredits(
  membershipId: string,
  payload: { amount: number; notes?: string }
) {
  const res = await api.post(
    `/master-admin/membership/memberships/${membershipId}/credits`,
    payload
  );
  return res.data?.data;
}

export async function fetchMembershipTransfers(status?: string) {
  const res = await api.get('/master-admin/membership/transfers', {
    params: status ? { status } : undefined,
  });
  return (res.data?.data || []) as any[];
}

export async function reviewMembershipTransfer(
  transferId: string,
  payload: { approve: boolean; reviewNotes?: string }
) {
  const res = await api.post(
    `/master-admin/membership/transfers/${encodeURIComponent(transferId)}/review`,
    payload
  );
  return res.data?.data;
}
